import styled from 'styled-components'
import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import useTranslation from '../../hooks/useTranslation'
import useOutsideClick from '../../hooks/useOutsideClick'
import DropDown from '../generic/DropDown'
import { Icon_Dots } from '../../styles/icons'

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.font.color};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.table.hover};
  }
  
  svg {
    width: 16px;
    height: 16px;
    margin: 0;
  }
`

const Badge = styled.span`
  background-color: ${({ theme }) => theme.btn.primary};
  color: white;
  border-radius: 1rem;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  min-width: 16px;
  text-align: center;
`

// ActionMenu will be portaled into <body>, so we use absolute positioning
const ActionMenu = styled.div`
  position: absolute;
  z-index: 1300;
  margin-top: 0px;
  margin-left: 0; /* Adjust to position menu to the left of the button */
`

const ActionContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ListActionsButton = ({ selectedCount, onAction, menuItems }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const containerRef = useRef(null)

  const handleToggleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const rect = e.currentTarget.getBoundingClientRect()
    setMenuPosition({
      top: rect.bottom + window.scrollY, // position below the button
      left: rect.right + window.scrollX , // align right edge with button, accounting for menu width
    })

    setIsOpen(prev => !prev)
  }

  const handleActionClick = (e, menuItem) => {
    e.preventDefault()
    e.stopPropagation()

    if (onAction) {
      onAction(menuItem)
    }
    setIsOpen(false)
  }

  // Use the existing useOutsideClick hook
  useOutsideClick(containerRef, () => setIsOpen(false), 'normal')

  // With nothing selected the menu is open only to the actions that do not need a
  // selection, so a delete cannot be offered with no rows behind it.
  const availableItems = selectedCount > 0
    ? menuItems
    : menuItems.filter(menuItem => menuItem.requiresSelection === false)

  if (availableItems.length === 0) return null

  return (
    <ActionContainer ref={containerRef}>
      <DropDown>
        <ActionButton
          type="button"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={handleToggleClick}
          $isOpen={isOpen}
        >
          {Icon_Dots}
          {selectedCount > 0 && <Badge>{selectedCount}</Badge>}
        </ActionButton>
        {isOpen &&
          createPortal(
            <ActionMenu style={{ top: menuPosition.top, left: menuPosition.left }}>
              <DropDown.Menu role="menu">
                {availableItems.map((menuItem, index) => (
                  <DropDown.Item
                    key={menuItem.action || index}
                    href="#"
                    onClick={(e) => handleActionClick(e, menuItem)}
                  >
                    {t(menuItem.label)}
                  </DropDown.Item>
                ))}
              </DropDown.Menu>
            </ActionMenu>,
            document.body
          )}
      </DropDown>
    </ActionContainer>
  )
}

export default ListActionsButton
